import { ColumnDef } from "@tanstack/react-table";
import React, { useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import * as z from "zod";

import { UrlSerializer } from "@/hooks/usePersistedState";
import { IndeterminateCheckbox } from "./IndeterminateCheckbox";

/**
 * The visibility state of each column. Note that this is a restriction on the type used by react-table, because columns
 * are either undefined (visible by default) or false (hidden). There is no option to explicitly set a column to true (visible).
 * This limitation is beneficial for our use case because we are saving the visibility state and this simplifies equality
 * comparisons and gives cleaner URLs when encoding the visibility state into the URL.
 */
export type VisibilityState = Record<string, false>;

export const VisibilityState: z.Schema<VisibilityState> = z.record(z.string(), z.literal(false));

/**
 * URL serializer for column visibility state. Encodes hidden column IDs as a comma-separated list instead of JSON,
 * producing URLs like `?filter=col1,col2,col3` instead of `?filter=%7B%22col1%22%3Afalse%2C%22col2%22%3Afalse%7D`.
 */
export const visibilityStateUrlSerializer: UrlSerializer<VisibilityState> = {
  serialize: (state) => Object.keys(state).join(","),
  deserialize: (encoded) =>
    Object.fromEntries(
      encoded
        .split(",")
        .filter(Boolean)
        .map((key) => [key, false as const]),
    ),
};

interface ColumnVisibilityToggleProps<TData = unknown> {
  columns: ColumnDef<TData>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (newVisibility: VisibilityState) => void;
}

/**
 * Represents a node in the column hierarchy tree for visibility toggling.
 */
type ColumnTreeNode = ColumnTreeParent | ColumnTreeLeaf;

interface ColumnTreeParent extends ColumnTreeNodeBase {
  type: "parent";
  children: ColumnTreeNode[];

  /** The state of the checkbox for this node. Undefined means indeterminate */
  checked: boolean | undefined;
}

interface ColumnTreeLeaf extends ColumnTreeNodeBase {
  type: "leaf";
  children: never[];

  /** The state of the checkbox for this node. Leaf nodes cannot be indeterminate */
  checked: boolean;
}

interface ColumnTreeNodeBase {
  id: string;

  /** Display label to use for the column. This is the column header, if provided as a string, falling back to the column id */
  label: string;
}

/**
 * Dropdown UI for toggling column visibility, supporting hierarchy and indeterminate state.
 */
export function ColumnVisibilityDropdown<TData = unknown>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
}: ColumnVisibilityToggleProps<TData>): React.ReactElement | null {
  // Convert the column definitions into a simplified tree structure for rendering
  const { tree, numLeaves } = buildColumnTree(columns, columnVisibility);

  // Count the number of hidden vs visible columns
  const numHiddenColumns = Object.values(columnVisibility).length;
  const numVisibleColumns = numLeaves - numHiddenColumns;

  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle variant="secondary" size="sm">
        Columns {numHiddenColumns > 0 ? `(${numVisibleColumns.toFixed()} / ${numLeaves.toFixed()} selected)` : ""}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto", padding: "0.5rem" }}>
        {tree.map((node) => (
          <Dropdown.ItemText key={node.id}>
            <ColumnVisibilityNode
              node={node}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={onColumnVisibilityChange}
            />
          </Dropdown.ItemText>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

/**
 * Recursively renders a node in the column visibility tree.
 */
const ColumnVisibilityNode = ({
  node,
  columnVisibility,
  onColumnVisibilityChange,
}: {
  node: ColumnTreeNode;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (newVisibility: VisibilityState) => void;
}) => {
  // Define a callback for the checkbox change event
  const onChange = useCallback(() => {
    const newColumnVisibility = toggleColumnVisibility(node, columnVisibility);
    onColumnVisibilityChange(newColumnVisibility);
  }, [node, columnVisibility, onColumnVisibilityChange]);

  // Do not render parent nodes that have no children
  if (node.type === "parent" && !node.children.length) return null;

  return (
    <>
      <IndeterminateCheckbox
        id={`filter-col-visibility-${node.id}`}
        checked={node.checked}
        onChange={onChange}
        label={node.label}
      />
      {node.children.length > 0 && (
        <div className="ms-3">
          {node.children.map((child) => (
            <ColumnVisibilityNode
              key={child.id}
              node={child}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={onColumnVisibilityChange}
            />
          ))}
        </div>
      )}
    </>
  );
};

/**
 * Recursively build a tree structure from the column definitions for hierarchical visibility toggling.
 */
function buildColumnTree<TData>(
  columns: ColumnDef<TData>[],
  visibility: VisibilityState,
): { tree: ColumnTreeNode[]; numLeaves: number } {
  let numLeaves = 0;
  const tree = columns
    .filter((col) => col.enableHiding !== false)
    .map((col) => {
      // Determine the unique ID for the column
      // See: https://tanstack.com/table/latest/docs/guide/column-defs#unique-column-ids
      const id =
        "id" in col && col.id ? col.id
        : "accessorKey" in col ? col.accessorKey.toString().replaceAll(".", "_")
        : "header" in col && typeof col.header === "string" ? col.header
        : undefined;

      // Determine the column type
      const isParent = "columns" in col && col.columns;
      const type = isParent ? ("parent" as const) : ("leaf" as const);
      if (!isParent) numLeaves++;

      // Recursively build the tree for child columns
      const recursiveResult = buildColumnTree("columns" in col ? (col.columns ?? []) : [], visibility);
      const children = recursiveResult.tree;
      numLeaves += recursiveResult.numLeaves;

      return {
        type,
        id,
        label: typeof col.header === "string" ? col.header : (col.id ?? ""),
        children,
      } as Omit<ColumnTreeParent, "checked"> | Omit<ColumnTreeLeaf, "checked">;
    })
    .map(
      (node) =>
        ({
          ...node,
          checked: getNodeCheckedState(node, visibility),
        }) as ColumnTreeNode,
    );
  return { tree, numLeaves };
}

/**
 * Determine the checked state for a node based on it's visibility or the visibility of its children.
 */
function getNodeCheckedState(
  node: Omit<ColumnTreeParent, "checked"> | Omit<ColumnTreeLeaf, "checked">,
  columnVisibility: VisibilityState,
): boolean | undefined {
  // If this is a leaf node, return its visibility state directly
  // NOTE: If a visibility state for the column is not explicitly set, it defaults to true
  if (node.type === "leaf") return columnVisibility[node.id] !== false;

  // Parent node visibilities are determined by their children
  // If all children have the same visibility value, return that value
  // If children have mixed visibility, return undefined (indeterminate)
  const uniqueChildVisibilities = new Set(node.children.map((child) => getNodeCheckedState(child, columnVisibility)));
  return uniqueChildVisibilities.size === 1 ? uniqueChildVisibilities.values().next().value : undefined;
}

/**
 * Toggle the visibility for a column and its children in the column tree.
 * @param node - The column node to toggle.
 * @param columnVisibility - The current visibility state of the columns. This object is not modified.
 * @returns A new visibility state object with the updated visibility.
 */
function toggleColumnVisibility(node: ColumnTreeNode, columnVisibility: VisibilityState): VisibilityState {
  const newVisibility = { ...columnVisibility };
  toggleColumnVisibilityInternal(node, newVisibility);
  console.log("newVisibility", newVisibility);
  return newVisibility;
}

/**
 * Toggle the visibility for a column and its children in the column tree, modifying the visibility state in-place.
 */
function toggleColumnVisibilityInternal(
  node: ColumnTreeNode,
  columnVisibility: VisibilityState,
  forceValue?: boolean,
): void {
  if (node.type === "leaf") {
    // For leaf nodes, update the node's visibility directly
    // NOTE: If visibility is true, set the value to undefined to indicate that the column is visible
    const newVisibility = forceValue !== undefined ? forceValue : !node.checked;
    if (newVisibility) {
      delete columnVisibility[node.id]; // eslint-disable-line @typescript-eslint/no-dynamic-delete
    } else {
      columnVisibility[node.id] = false;
    }
  } else {
    // Determine the new visibility state for the parent node based on the current state
    // NOTE: Parent nodes can also be indeterminate (undefined) if their children have mixed visibility, in which case,
    //       we set the parent node to be visible (true) when toggling.
    const newVisibility =
      forceValue !== undefined ? forceValue
      : node.checked === undefined ? true
      : !node.checked;

    // Recursively update visibility for all children to the same value
    node.children.forEach((child) => {
      toggleColumnVisibilityInternal(child, columnVisibility, newVisibility);
    });
  }
}
